// default elevations in the openings tables and printouts: an opening with no drw elevation of a
// kind renders a default (a rendering with no elevation link). See
// elevationTool/default-elevations-plan.md.

// the elevation name a default shows (topening.DrwDefaultElevationName).
export const drwDefaultElevationName = "DEFAULT";

export type DrwKind = "screen" | "door" | "hardware";

type DrwElevationRow = {
    drwScreenElevation?: number | null;
    drwDoorElevation?: number | null;
    drwHardwareElevation?: number | null;
    drwScreenElevationName?: string | null;
    drwDoorElevationName?: string | null;
    drwHardwareElevationName?: string | null;
    drwScreenRendering?: number | null;
    drwDoorRendering?: number | null;
    drwHardwareRendering?: number | null;
    drwScreenPrimaryOpening?: number | null;
    screenElevation?: string | null;
    screenElevationFile?: number | null;
    doorElevation?: string | null;
    doorElevationFile?: number | null;
    inactiveDoorElevation?: string | null;
    inactiveDoorElevationFile?: number | null;
    archived?: boolean;
};

function fieldsFor(row: DrwElevationRow, kind: DrwKind) {
    switch(kind) {
        case "screen":
            return {link: row.drwScreenElevation, name: row.drwScreenElevationName, rendering: row.drwScreenRendering};
        case "door":
            return {link: row.drwDoorElevation, name: row.drwDoorElevationName, rendering: row.drwDoorRendering};
        case "hardware":
            return {link: row.drwHardwareElevation, name: row.drwHardwareElevationName, rendering: row.drwHardwareRendering};
    }
}

export function isDrwDefault(row: DrwElevationRow, kind: DrwKind): boolean {
    const f = fieldsFor(row, kind);
    return !f.link && !!f.rendering;
}

// true when the opening uses a legacy (file-based) elevation column. Such an opening gets no
// defaults.
function legacyElevationUsed(row: DrwElevationRow): boolean {
    return !!(row.screenElevation || row.screenElevationFile
        || row.doorElevation || row.doorElevationFile
        || row.inactiveDoorElevation || row.inactiveDoorElevationFile);
}

// true when the opening gets a default for the kind, rendered or not yet (the same rules as
// drwrender defaultEligible). disabled is the project's disableDrwDefaultAutoGen.
export function drwDefaultEligible(row: DrwElevationRow, kind: DrwKind, disabled: boolean): boolean {
    if(disabled || row.archived || legacyElevationUsed(row) || fieldsFor(row, kind).link) return false;
    if(kind === "screen" && row.drwScreenPrimaryOpening) return false;
    return true;
}

// true when the opening's elevation of the kind is its default: rendered already, or eligible and
// not rendered yet. The tool draws it from the opening either way.
export function rendersDrwDefault(row: DrwElevationRow, kind: DrwKind, disabled: boolean): boolean {
    return isDrwDefault(row, kind) || drwDefaultEligible(row, kind, disabled);
}

// the drw elevation name to print for a kind: the linked elevation's name, DEFAULT for a default,
// else "".
export function drwElevationName(row: DrwElevationRow, kind: DrwKind): string {
    if(isDrwDefault(row, kind)) return drwDefaultElevationName;
    const f = fieldsFor(row, kind);
    return f.link ? (f.name || "") : "";
}
